import React, { type FC, type PropsWithChildren, type ReactElement, type ReactNode, useId, useRef, useState } from 'react'
import { Box, Paper, Portal, Slide, type SxProps, type Theme, ThemeProvider, Tooltip, type TooltipProps, Typography, useMediaQuery } from '@mui/material'

import type { AnyFunction, ReadonlyRecord } from '../../../../utils'

import { Translation } from '../../../../domain'

import { useTranslator } from '../../Internationalization'
import { RIGHT_PANEL_SHADOW } from '../../theme'
import { ChevronDownIcon, ChevronUpIcon, CloseIcon, MaximizeIcon } from '../Branding'
import { IconButton } from '../Button'
import { useIsMobile } from '../Mobile'
import { ABOVE_BODY_Z_INDEX, BODY_Z_INDEX, type PanelDrag, PanelGrip, toPanelWidthStyle } from './Grip'
import { useRecordPanelOutlet } from './Outlet'
import { useRecordPanelShortcuts } from './Shortcuts'

const MIN_WIDTH = 600
const MAX_WIDTH = 720
/** In vw */
const PREFERRED_WIDTH = 33
const HEADER_HEIGHT = 28
/** In px */
const SNAP_OUTLINE_WIDTH = 2
/** In px: fully rounds the ends of a one-line pill */
const PILL_RADIUS = 999

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

const panelStyle: SxProps<Theme> = {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: (theme) => theme.zIndex.appBar,
    width: { xs: '100%', md: `min(100%, clamp(${MIN_WIDTH}px, ${PREFERRED_WIDTH}vw, ${MAX_WIDTH}px))` },
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    py: 6,
    px: 4,
    boxShadow: RIGHT_PANEL_SHADOW,
}
/** Letting go would open the page */
const snappingStyle: SxProps<Theme> = (theme) => ({
    outline: `${SNAP_OUTLINE_WIDTH}px solid ${theme.palette.primary.main}`,
    outlineOffset: `${-SNAP_OUTLINE_WIDTH}px`,
})
const snapPillStyle: SxProps<Theme> = {
    position: 'absolute',
    top: 3,
    left: '50%',
    zIndex: ABOVE_BODY_Z_INDEX,
    transform: 'translateX(-50%)',
    px: 3,
    py: 1,
    borderRadius: `${PILL_RADIUS}px`,
    bgcolor: 'primary.700',
    color: 'common.white',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
}
const headerStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 2, height: `${HEADER_HEIGHT}px`, flexShrink: 0 }
const titleStyle: SxProps<Theme> = { flex: 1, minWidth: 0, color: 'grey.900' }
const positionStyle: SxProps<Theme> = { color: 'grey.500', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const bodyStyle: SxProps<Theme> = { position: 'relative', zIndex: BODY_Z_INDEX, flex: 1, minHeight: 0, overflowY: 'auto' }

type TooltipPopperProps = Exclude<NonNullable<NonNullable<TooltipProps['slotProps']>['popper']>, AnyFunction>

const withFixedStrategy = (popper: TooltipPopperProps | undefined): TooltipPopperProps => ({
    ...popper,
    popperOptions: { ...popper?.popperOptions, strategy: 'fixed' },
})

/**
 * A tooltip's popper is absolutely positioned in the document's body, outside the outlet's clipping: one shown while the panel slid out followed its anchor
 * past the page's edge and the page's scrollbars flashed. A fixed popper can't extend the page.
 */
const withFixedTooltips = (outerTheme: Theme): Theme => {
    const tooltip = outerTheme.components?.MuiTooltip
    const defaultProps = tooltip?.defaultProps
    const popper = defaultProps?.slotProps?.popper
    return {
        ...outerTheme,
        components: {
            ...outerTheme.components,
            MuiTooltip: {
                ...tooltip,
                defaultProps: {
                    ...defaultProps,
                    slotProps: {
                        ...defaultProps?.slotProps,
                        popper: typeof popper === 'function' ? (ownerState) => withFixedStrategy(popper(ownerState)) : withFixedStrategy(popper),
                    },
                },
            },
        },
    }
}

type HeaderAction = 'previous' | 'next' | 'expand' | 'close'

const keyShortcuts: ReadonlyRecord<HeaderAction, string> = { previous: 'ArrowUp', next: 'ArrowDown', expand: 'Shift+Enter', close: 'Escape' }

const HeaderButton: FC<Readonly<{ action: HeaderAction, icon: ReactElement, onClick: VoidFunction | undefined }>> = ({ action, icon, onClick }) => {
    const translator = useTranslator()

    return (
        <Tooltip title={translator.formatTranslation(Translation.RECORD_PANEL_ACTION_TOOLTIP, { action })}>
            <IconButton
                data-testid={`record-panel-${action}`}
                variant="ghost"
                size="small"
                aria-label={translator.formatTranslation(Translation.RECORD_PANEL_ACTION, { action })}
                aria-keyshortcuts={keyShortcuts[action]}
                aria-disabled={onClick ? undefined : true}
                onClick={onClick}
            >
                {icon}
            </IconButton>
        </Tooltip>
    )
}

/** Zero-based `index` */
type RecordPanelPosition = Readonly<{ index: number, total: number }>

type Props =
    & Readonly<{
        open: boolean
        title: ReactNode
        /** Hidden when `null` */
        position: RecordPanelPosition | null
        /** The button is disabled without it, e.g. on the first record */
        onPrevious?: VoidFunction
        /** The button is disabled without it, e.g. on the last record */
        onNext?: VoidFunction
        /** The button is hidden without it, and on mobile (narrow screens) */
        onExpand?: VoidFunction
        onClose: VoidFunction
        /** Called once the exit transition is over and the content is unmounted */
        onExited?: VoidFunction
        /** In px, set through the grip; `null` or none for the default width */
        width?: number | null
        /** Shows the grip on the panel's left edge, except on mobile (narrow screens); `null` asks for the default width */
        onWidthChange?: (width: number | null) => void
    }>
    & PropsWithChildren

/**
 * A non-modal panel over the right side of the page, rendered into the `RecordPanelOutletProvider`'s outlet, or in place without a provider.
 * While open, ↑, ↓, Shift+Enter and Escape act as its buttons. Only the body (`children`) scrolls; it's unmounted once the panel has left.
 * With `onWidthChange`, its left edge is a grip (see `PanelGrip`); a `width` set earlier is kept within the outlet.
 */
export const RecordPanel: FC<Props> = ({ open, title, position, onPrevious, onNext, onExpand, onClose, onExited, width, onWidthChange, children }) => {
    const translator = useTranslator()
    const outlet = useRecordPanelOutlet()
    const titleId = useId()
    const isMobile = useIsMobile()
    const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY)
    const shownOnExpand = isMobile ? undefined : onExpand
    const headerRef = useRef<HTMLDivElement>(null)
    const [drag, setDrag] = useState<PanelDrag | null>(null)

    useRecordPanelShortcuts(open && outlet !== null, { onPrevious, onNext, onExpand: shownOnExpand, onClose }, headerRef)

    const widthStyle = isMobile
        ? undefined
        : drag
        ? `${drag.width}px`
        : typeof width === 'number'
        ? toPanelWidthStyle(width, shownOnExpand !== undefined)
        : undefined

    /** Waits for the outlet rather than mounting in place first and then again in it */
    if (outlet === null) {
        return null
    }

    return (
        <Portal container={outlet} disablePortal={outlet === undefined}>
            <Slide in={open} direction="left" timeout={reducedMotion ? 0 : undefined} mountOnEnter unmountOnExit onExited={onExited}>
                <Paper
                    data-testid="record-panel"
                    component="section"
                    aria-labelledby={titleId}
                    aria-roledescription={translator.formatTranslation(Translation.RECORD_PANEL)}
                    square
                    elevation={0}
                    style={widthStyle === undefined ? undefined : { width: widthStyle }}
                    sx={[panelStyle, drag?.snapping === true && snappingStyle]}
                >
                    {open && !isMobile && onWidthChange && (
                        <PanelGrip drag={drag} widthStyle={widthStyle} onExpand={shownOnExpand} onDrag={setDrag} onWidthChange={onWidthChange} />
                    )}
                    {drag?.snapping && (
                        <Typography data-testid="record-panel-snap" aria-hidden variant="caption" sx={snapPillStyle}>
                            {translator.formatTranslation(Translation.RECORD_PANEL_SNAP)}
                        </Typography>
                    )}
                    <ThemeProvider theme={withFixedTooltips}>
                        <Box ref={headerRef} sx={headerStyle}>
                            <Typography id={titleId} data-testid="record-panel-title" component="h2" variant="subtitle2" noWrap sx={titleStyle}>
                                {title}
                            </Typography>
                            {position && (
                                <Typography data-testid="record-panel-position" variant="caption" sx={positionStyle}>
                                    {translator.formatTranslation(Translation.RECORD_PANEL_POSITION, { index: position.index + 1, total: position.total })}
                                </Typography>
                            )}
                            <HeaderButton action="previous" icon={<ChevronUpIcon />} onClick={onPrevious} />
                            <HeaderButton action="next" icon={<ChevronDownIcon />} onClick={onNext} />
                            {shownOnExpand && <HeaderButton action="expand" icon={<MaximizeIcon />} onClick={shownOnExpand} />}
                            <HeaderButton action="close" icon={<CloseIcon />} onClick={onClose} />
                        </Box>
                        <Box data-testid="record-panel-body" sx={bodyStyle}>
                            {children}
                        </Box>
                    </ThemeProvider>
                </Paper>
            </Slide>
        </Portal>
    )
}
